import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useSite } from "@/lib/site-context";
import { CheckCircle2 } from "lucide-react";
import DateTimePicker, { useAvailability } from "@/components/DateTimePicker";

const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  const base = API.replace(/\/api$/, "");
  return u.startsWith("/api/") ? `${base}${u}` : u;
};

export default function Book() {
  const { vehicles, rates } = useSite();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [submitted, setSubmitted] = useState(null);

  const [form, setForm] = useState({
    vehicle_id: params.get("vehicle") || "",
    pickup_location: "",
    return_location: "",
    pickup_datetime: "",
    return_datetime: "",
    insurance: "Basic",
    extras: [],
    full_name: "",
    email: "",
    phone: "",
    driver_age: "",
    notes: "",
  });

  useEffect(() => {
    if (form.vehicle_id && !form.return_location && form.pickup_location) {
      setForm((f) => ({ ...f, return_location: f.pickup_location }));
    }
  }, [form.vehicle_id]);

  const vehicle = useMemo(() => vehicles.find((v) => v.id === form.vehicle_id), [vehicles, form.vehicle_id]);
  const blocked = useAvailability(form.vehicle_id);
  const days = useMemo(() => {
    if (!form.pickup_datetime || !form.return_datetime) return 0;
    const a = new Date(form.pickup_datetime); const b = new Date(form.return_datetime);
    return Math.max(1, Math.ceil((b - a) / (1000 * 60 * 60 * 24)));
  }, [form.pickup_datetime, form.return_datetime]);

  const insuranceOpt = (rates?.insurance_options || []).find((i) => i.name === form.insurance);
  const extrasList = rates?.extras || [];

  const total = useMemo(() => {
    if (!vehicle || !days) return 0;
    let t = vehicle.daily_rate * days;
    if (insuranceOpt) t += (insuranceOpt.price_per_day || 0) * days;
    for (const name of form.extras) {
      const e = extrasList.find((x) => x.name === name);
      if (!e) continue;
      t += e.price_per_day ? e.price_per_day * days : (e.price_flat || 0);
    }
    return t;
  }, [vehicle, days, insuranceOpt, form.extras, extrasList]);

  const toggleExtra = (name) => {
    setForm((f) => ({ ...f, extras: f.extras.includes(name) ? f.extras.filter((x) => x !== name) : [...f.extras, name] }));
  };

  const submit = async () => {
    if (!form.vehicle_id) { toast.error("Please select a vehicle."); return; }
    if (!form.pickup_datetime || !form.return_datetime) { toast.error("Please pick your pick-up and return dates."); return; }
    if (form.pickup_datetime >= form.return_datetime) { toast.error("Return must be after pick-up."); return; }
    if (!form.pickup_location || !form.return_location) { toast.error("Please enter both pick-up and return locations."); return; }
    if (!form.full_name || !form.email || !form.phone) { toast.error("Please complete your name, email and phone."); return; }
    try {
      const payload = {
        ...form,
        vehicle_name: vehicle?.name,
        driver_age: form.driver_age ? parseInt(form.driver_age) : null,
        estimated_total: total,
      };
      const r = await api.post("/bookings", payload);
      setSubmitted(r.data);
      toast.success("Booking enquiry submitted! We'll be in touch shortly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit. Please try again.");
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="booking-success">
        <div className="w-16 h-16 rounded-full bg-[#D9A62E]/20 border border-[#D9A62E]/40 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-[#D9A62E]" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-extrabold text-white">Enquiry Received</h1>
        <p className="mt-3 text-[#A3A3A3]">Thanks {submitted.full_name.split(" ")[0]}. Your reference is <span className="text-[#D9A62E] font-mono-piki">#{submitted.id.slice(0, 8).toUpperCase()}</span>. We&apos;ll be in touch.</p>
        <p className="mt-2 text-sm text-[#737373]">Our team will confirm your booking and payment details within a few hours via {submitted.email}.</p>
        {/* eslint-disable-next-line */}
        <button onClick={() => nav("/")} className="mt-8 piki-gold-btn">Back to Home</button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#D9A62E] font-mono-piki font-semibold">Book a Car</div>
      <h1 className="mt-3 font-display text-4xl font-extrabold text-white">Reservation Enquiry</h1>
      <p className="mt-3 text-[#A3A3A3]">Complete the form below and we'll confirm availability and next steps.</p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* STEP 1 */}
          <div className="piki-card p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-full bg-[#D9A62E] text-[#050505] text-sm font-bold flex items-center justify-center">1</div>
              <h2 className="font-display font-bold text-lg text-white">Trip & Vehicle</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-xs text-[#A3A3A3] mb-1.5 block">Vehicle</label>
                <select value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} className="piki-input text-sm" data-testid="booking-form-vehicle-select">
                  <option value="">Select a vehicle…</option>
                  {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} — ${v.daily_rate}/day</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] mb-1.5 block">Pick-up Location</label>
                <input className="piki-input text-sm" value={form.pickup_location} onChange={(e) => setForm({ ...form, pickup_location: e.target.value })} placeholder="Airport, city, hotel…" data-testid="booking-form-pickup-location-input" />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] mb-1.5 block">Return Location</label>
                <input className="piki-input text-sm" value={form.return_location} onChange={(e) => setForm({ ...form, return_location: e.target.value })} placeholder="Same as pick-up…" data-testid="booking-form-return-location-input" />
              </div>
              <DateTimePicker
                label="Pick-up Date & Time"
                value={form.pickup_datetime}
                onChange={(v) => setForm({ ...form, pickup_datetime: v })}
                blocked={blocked}
                testId="booking-form-pickup-date-input"
              />
              <DateTimePicker
                label="Return Date & Time"
                value={form.return_datetime}
                onChange={(v) => setForm({ ...form, return_datetime: v })}
                blocked={blocked}
                minDate={form.pickup_datetime}
                testId="booking-form-return-date-input"
              />
              {form.vehicle_id && blocked.length > 0 && (
                <div className="sm:col-span-2 text-xs text-[#A3A3A3] bg-[#0F0F10] border border-[#2E2E2E] rounded-lg p-3">
                  <span className="text-[#D9A62E] font-semibold">Heads up:</span> This vehicle has {blocked.length} existing reservation{blocked.length > 1 ? "s" : ""}. Unavailable dates are disabled on the calendar.
                </div>
              )}
            </div>
          </div>

          {/* STEP 2 */}
          <div className="piki-card p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-full bg-[#D9A62E] text-[#050505] text-sm font-bold flex items-center justify-center">2</div>
              <h2 className="font-display font-bold text-lg text-white">Insurance & Extras</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(rates?.insurance_options || []).map((ins) => (
                <label key={ins.name} className={`cursor-pointer p-4 rounded-xl border transition-all ${form.insurance === ins.name ? "border-[#D9A62E] bg-[#D9A62E]/5" : "border-[#2E2E2E] hover:border-[#525252]"}`}>
                  <input type="radio" name="ins" value={ins.name} checked={form.insurance === ins.name} onChange={() => setForm({ ...form, insurance: ins.name })} className="hidden" data-testid={`booking-form-insurance-${ins.name.toLowerCase().replace(/ /g, "-")}-radio`} />
                  <div className="font-semibold text-white">{ins.name}</div>
                  <div className="text-[#D9A62E] font-mono-piki text-sm mt-1">${ins.price_per_day}/day</div>
                  <div className="text-[10px] text-[#737373] mt-1">{ins.note}</div>
                </label>
              ))}
            </div>
            <div className="mt-6">
              <div className="text-xs text-[#A3A3A3] uppercase tracking-wider font-mono-piki mb-3">Optional Add-ons</div>
              <div className="flex flex-wrap gap-2">
                {(rates?.extras || []).map((e) => (
                  <button key={e.name} type="button" onClick={() => toggleExtra(e.name)} data-testid={`booking-form-extra-${e.name.toLowerCase().replace(/ /g, "-")}`} className={`px-3 py-2 rounded-full text-xs font-semibold border transition-all ${form.extras.includes(e.name) ? "bg-[#D9A62E] text-[#050505] border-[#D9A62E]" : "border-[#2E2E2E] text-[#D4D4D4] hover:border-[#D9A62E]"}`}>
                    {e.name} +${e.price_per_day ?? e.price_flat}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* STEP 3 */}
          <div className="piki-card p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-full bg-[#D9A62E] text-[#050505] text-sm font-bold flex items-center justify-center">3</div>
              <h2 className="font-display font-bold text-lg text-white">Customer Details</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#A3A3A3] mb-1.5 block">Full Name *</label>
                <input className="piki-input text-sm" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="booking-form-fullname-input" />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] mb-1.5 block">Email *</label>
                <input type="email" className="piki-input text-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-form-email-input" />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] mb-1.5 block">Phone *</label>
                <input className="piki-input text-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-form-phone-input" />
              </div>
              <div>
                <label className="text-xs text-[#A3A3A3] mb-1.5 block">Driver Age</label>
                <input type="number" className="piki-input text-sm" value={form.driver_age} onChange={(e) => setForm({ ...form, driver_age: e.target.value })} data-testid="booking-form-driver-age-input" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-[#A3A3A3] mb-1.5 block">Notes / Special Requests</label>
                <textarea rows={3} className="piki-input text-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-form-notes-input" />
              </div>
            </div>
          </div>
        </div>

        {/* SUMMARY */}
        <aside className="lg:col-span-1">
          <div className="piki-card p-6 sticky top-24">
            <div className="text-xs uppercase tracking-widest text-[#D9A62E] font-mono-piki font-semibold">Booking Summary</div>
            <h3 className="font-display font-bold text-xl text-white mt-2">{vehicle?.name || "Select a vehicle"}</h3>
            {vehicle && <img src={resolveUrl(vehicle.primary_image)} alt="" className="mt-3 w-full aspect-video object-cover rounded-lg" />}
            <div className="mt-5 space-y-2 text-sm">
              <Row label="Days" value={days || "—"} />
              <Row label="Vehicle" value={vehicle ? `$${vehicle.daily_rate} × ${days || 0}` : "—"} />
              <Row label="Insurance" value={insuranceOpt ? `${insuranceOpt.name} · $${(insuranceOpt.price_per_day || 0) * days}` : "—"} />
              {form.extras.length > 0 && <Row label="Extras" value={`${form.extras.length} selected`} />}
            </div>
            <div className="mt-5 pt-5 border-t border-[#2E2E2E] flex items-center justify-between">
              <span className="text-sm text-[#A3A3A3]">Estimated Total</span>
              <span className="font-mono-piki text-3xl font-extrabold text-[#D9A62E]" data-testid="booking-summary-total-price">${total.toFixed(0)}</span>
            </div>
            <button onClick={submit} className="mt-5 piki-gold-btn w-full" data-testid="booking-form-submit-btn">
              Submit Enquiry
            </button>
            <p className="mt-3 text-[10px] text-[#737373] text-center">Estimated total. Final rate confirmed by our team.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#A3A3A3]">{label}</span>
      <span className="text-white font-medium">{value}</span>
    </div>
  );
}
